import { useCallback, useEffect, useState } from "react";
import { Download, Paperclip } from "lucide-react";
import { toast } from "sonner";
import type { components } from "@dolfin/api-public";
import { useDolfinClient } from "@/components/DolfinProvider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatBytes } from "@/lib/format";

type BillAttachment = components["schemas"]["BillAttachmentResponse"];

/** Signed links live 15 minutes, so each click fetches a fresh list rather than reusing the page-load one. */
export function BillAttachmentsCard({ billId }: { billId: string }) {
  const api = useDolfinClient();
  const [attachments, setAttachments] = useState<BillAttachment[]>([]);
  const [downloading, setDownloading] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { data, error } = await api.GET("/v1/bills/{id}/attachments", {
      params: { path: { id: billId } },
    });
    if (error) throw error;
    return data ?? [];
  }, [api, billId]);

  useEffect(() => {
    let cancelled = false;
    load()
      .then((list) => {
        if (!cancelled) setAttachments(list);
      })
      // Quiet, like the faults card: the bill is fully usable without its attachments.
      .catch((error) => console.error("Failed to load the bill's attachments:", error));
    return () => {
      cancelled = true;
    };
  }, [load]);

  const download = async (id: string) => {
    setDownloading(id);
    try {
      const fresh = await load();
      setAttachments(fresh);
      const url = fresh.find((a) => a.id === id)?.downloadUrl;
      if (!url) {
        toast.error("This attachment can't be downloaded right now");
        return;
      }
      // The response is Content-Disposition: attachment, so navigating saves the file and leaves the page.
      window.location.assign(url);
    } catch {
      toast.error("Failed to download the attachment");
    } finally {
      setDownloading(null);
    }
  };

  if (attachments.length === 0) return null;

  return (
    <Card className="bg-card/60 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Paperclip className="size-4" />
          {attachments.length === 1 ? "1 attachment" : `${attachments.length} attachments`}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="divide-y">
          {attachments.map((a) => (
            <li key={a.id} className="flex items-center gap-3 py-2 first:pt-0 last:pb-0">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium" title={a.fileName}>
                  {a.fileName}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {[a.description, a.size ? formatBytes(a.size) : null].filter(Boolean).join(" · ")}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => download(a.id)}
                disabled={downloading !== null || !a.downloadUrl}
              >
                <Download className="size-4" />
                Download
              </Button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
